import {IModel} from "../../../shared/view-model/imodel.interface";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {
  ApolloQueryResult,
  MutationOptions,
  MutationQueryReducersMap,
  Subscription,
  WatchQueryOptions
} from "apollo-client";
import {isNullOrUndefined, isUndefined} from "util";
import {NgRedux, select} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store/store";
import {SessionActions} from "../../../shared/actions/session.actions";
import * as _ from "lodash";
import {Observable} from "rxjs/Observable";
import {environment} from "../../../../environments/environment";
import {IUser} from "../../../shared/redux-store/session/session.types";
import {filter, finalize, map} from "rxjs/operators";
import {handleObservable} from "./handleObservable";

export const visbosFragments = {};

export function pruneEmpty(obj) {
  return function prune(current) {
    _.forOwn(current, function (value, key) {
      if (_.isUndefined(value) || _.isNull(value) || _.isNaN(value) ||
        //(_.isString(value) && _.isEmpty(value)) ||
        (_.isObject(value) && _.isEmpty(prune(value)))) {

        delete current[key];
      }
    });
    // remove any leftover undefined values from the delete
    // operation on an array
    if (_.isArray(current)) _.pull(current, undefined);

    return current;

  }(_.cloneDeep(obj));  // Do not modify the original object, create a clone instead
}

export function pruneTypename(obj) {
  return function prune(current) {
    _.forOwn(current, function (value, key) {
      if (_.isObject(value)) {
        prune(value);
      }
      if (_.isArray(current)) {
        _.forEach(current, prune)
      }
      if (key == '__typename') {
        delete current[key];
      }
    });
    // remove any leftover undefined values from the delete
    // operation on an array
    if (_.isArray(current)) _.pull(current, undefined);

    return current;

  }(_.cloneDeep(obj));  // Do not modify the original object, create a clone instead
}

export abstract class VisbosService {

  @select(['session', 'activeAdminUnit'])
  protected activeAdminUnitCuid$: Observable<string>;
  protected activeAdminUnitCuid: string;

  @select(['session', 'user', 'cuid'])
  protected currentUserCuid$: Observable<string>;
  protected currentUserCuid: string;

  @select(['session', 'user'])
  protected currentUser$: Observable<IUser>;
  protected currentUser: IUser;

  constructor(protected apollo: Apollo, protected ngRedux: NgRedux<IAppState>, protected sessionActions: SessionActions) {
    this.currentUserCuid$.subscribe(user => this.currentUserCuid = user);
    this.activeAdminUnitCuid$.subscribe(au => this.activeAdminUnitCuid = au);
    this.currentUser$.subscribe(user => this.currentUser = user);
  }

  public fetchItems<T>(options: WatchQueryOptions, loadingVisible: boolean = false): ApolloQueryObservable<T> {

    /*if (!this.ngRedux.getState().session.token || !this.ngRedux.getState().session.loggedIn) {
     console.warn("Not logged in, canceling query!");
     return Observable.empty() as ApolloQueryObservable<T>;
     }*/

    if (loadingVisible && this.sessionActions) {
      this.sessionActions.initAsyncAction();
    }

    let opt = Object.assign({}, options);

    if (environment.network.forceFetch) {
      opt.fetchPolicy = 'network-only';
    } else {
      opt.fetchPolicy = opt.fetchPolicy ? opt.fetchPolicy : 'cache-and-network';
    }

    let query = this.apollo.watchQuery<T>(opt).pipe(
      handleObservable(),
      filter((res: any) => (res.data != undefined)),
      map((res: any) => {
        if (loadingVisible && this.sessionActions && res.loading === true) {
          this.sessionActions.initAsyncAction();
        }
        if (loadingVisible && this.sessionActions && res.loading === false) {
          this.sessionActions.finishAsyncAction();
        }
        return res;
      }),
      finalize(() => {
        if (loadingVisible && this.sessionActions) {
          this.sessionActions.finishAsyncAction();
        }
      }));
    /*
      .handleObservable()
      .filter((res: any) => (res.data != undefined))
      .map((res: any) => {
        if (loadingVisible && this.sessionActions && res.loading === true) {
          this.sessionActions.initAsyncAction();
        }
        if (loadingVisible && this.sessionActions && res.loading === false) {
          this.sessionActions.finishAsyncAction();
        }
        return res;
      })
      .finally(() => {
        if (loadingVisible && this.sessionActions) {
          this.sessionActions.finishAsyncAction();
        }
      });
      */

    return <ApolloQueryObservable<T>>query;
  }

  public fetchItemsOnce<T>(options: WatchQueryOptions, loadingVisible: boolean = false): Observable<ApolloQueryResult<T>> {

    if (loadingVisible && this.sessionActions) {
      this.sessionActions.initAsyncAction();
    }

    let opt = Object.assign({}, options);
    opt.fetchPolicy = 'network-only';

    let query = this.apollo.query<T>(opt).pipe(
      handleObservable(),
      filter((res: any) => (res.data != undefined)),
      map((res: any) => {
        if (loadingVisible && this.sessionActions && res.loading === true) {
          this.sessionActions.initAsyncAction();
        }
        if (loadingVisible && this.sessionActions && res.loading === false) {
          this.sessionActions.finishAsyncAction();
        }
        return res;
      }),
      finalize(() => {
        if (loadingVisible && this.sessionActions) {
          this.sessionActions.finishAsyncAction();
        }
      }));

    return query;
  }

  public prefetchItems(options: WatchQueryOptions): Subscription {

    return this.apollo.query(options).pipe(
      handleObservable(),
      filter((res: any) => (res.data != undefined))
    ).subscribe();

  }

  public mutate<T>(options: MutationOptions, loadingVisible: boolean = true): Observable<ApolloQueryResult<T>> {

    if (loadingVisible && this.sessionActions) {
      this.sessionActions.initAsyncAction();
    }

    let mutation = this.apollo.mutate<T>(options)
      .pipe(
        handleObservable(),
        filter((res: any) => (res.data != undefined)),
        map((res: any) => {
          if (loadingVisible && this.sessionActions && res.loading === true) {
            this.sessionActions.initAsyncAction();
          }
          if (loadingVisible && this.sessionActions && res.loading === false) {
            this.sessionActions.finishAsyncAction();
          }
          return res;
        }),
        finalize(() => {
          if (loadingVisible && this.sessionActions) {
            this.sessionActions.finishAsyncAction();
          }
        }));

    return mutation as Observable<ApolloQueryResult<T>>;

  }

  public deleteItemsFromQueries(cuid: string, resultName: string, ...queryIds: string[]): MutationQueryReducersMap {

    let obj: MutationQueryReducersMap = {};

    for (let id of queryIds) {
      obj[id] = (prev: any, {mutationResult}) => {
        this.deleteFromQueryResult(prev[resultName], cuid);
        return prev;
      }
    }

    return obj;
  }

  public addItemsToQueries(prevResultName: string, mutationResultName: string, queryIds: string[]): MutationQueryReducersMap {

    let obj: MutationQueryReducersMap = {};

    for (let id of queryIds) {
      obj[id] = (prev: any, {mutationResult}: any) => {
        return this.addToQueryResult(prev, prevResultName, mutationResult.data[mutationResultName])
      }
    }

    return obj;
  }

  private addToQueryResult(prev: any, prevResultName, newItems: IModel[]) {

    const prevItems = prev[prevResultName];

    return Object.assign(prev, {
      [prevResultName]: [newItems, ...prevItems].sort(((n1: IModel, n2: IModel) => {
        if (n1.label < n2.label) {
          return 1;
        }
        if (n1.label > n2.label) {
          return -1;
        }
        return 0;
      }))
    });
  }


  private deleteFromQueryResult(queryItems: Array<IModel>, deleteableCuid: string) {
    let index = queryItems.findIndex(e => e.cuid == deleteableCuid);
    if (!isNullOrUndefined(index) && index > -1) {
      queryItems.splice(index, 1);
    }

    //TODO: Remove item from datastore
  }

  protected processData(data): any {
    let processedData = Object.assign({}, data);

    if (isUndefined(processedData.administration_unit_id)) {
      let adminUnitId = this.ngRedux.getState().session.activeAdminUnit;
      processedData.administration_unit_id = adminUnitId;
    }

    return processedData;
  }

}



// WEBPACK FOOTER //
// ./src/app/core/visbos-core/graphql/visbos.service.ts